import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, OnChanges, ViewChild, AfterViewInit, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatSelect, MatSelectTrigger } from "@angular/material/select";
import { map } from "rxjs/operators";
import { OverlayPopupService } from "../../core";
import { DescriptionOverlayComponent } from "../description-overlay/description-overlay.component";
import { MultiSelect } from "../models/material-common-models";
import { BucketItemsComponent } from "../../features/bucket-items/bucket-items.component";
import { MatListItem } from "@angular/material/list";
import { MatFormField, MatLabel, MatInput, MatSuffix } from "@angular/material/input";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { NgClass } from "@angular/common";
import { MatOption } from "@angular/material/autocomplete";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "app-multi-select-dropdown",
    templateUrl: "./multi-select-dropdown.component.html",
    styleUrls: ["./multi-select-dropdown.component.scss"],
    imports: [MatListItem, MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, MatSelectTrigger, MatTooltip, MatInput, MatSuffix, MatIconButton, MatCheckbox, NgClass, MatOption, MatButton, TranslatePipe]
})
export class MultiSelectDropdownComponent implements OnInit, OnChanges, AfterViewInit {
  private readonly overlayPopupService = inject(OverlayPopupService);
  private readonly destroyRef = inject(DestroyRef);

  /* #region Fields */
  @Input() public selectedItems: MultiSelect[];
  @Input() public items: MultiSelect[];
  @Input() public placeHolder: string;
  @Input() public custompanelClass = "";
  @Input() public floatLabel: string;
  @Input() public selectAllLabel: string = "Select all";
  @Input() public selectAllRequired: boolean = false;
  @Input() public mode: string;
  @Input() public requiredLabel: boolean = false;
  @Input() public showClearBox: Boolean = false;
  @Input() public showSearch: boolean = false;
  @Output() public clearOptions: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public selectionChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public selectedOptions = new EventEmitter();
  public IsChecked: boolean;
  @Input() public selectFormControl = new FormControl();
  public searchTextboxControl = new FormControl();
  readonly filteredOptions = signal<any[]>([]);
  public disableSelection:boolean=false;
  public disableClear:boolean=false;
  @ViewChild("selector") selector: MatSelect;
  public selectAllChecked : boolean = false;
  public selectAllindeterminate : boolean = false;
  @Input() public idPrefix?: string;

  private displayItems: MultiSelect[]=[];
  public displayItemsForSearch:MultiSelect[]=[];
  public showSelectAll:boolean = true;
  public firstOptionChecked: boolean = false;
  private static instanceCounter = 0;
  private readonly instanceSuffix: string;
  private readonly defaultFeatureToken = "shared";
  private readonly defaultSurfaceToken = "multi-select-dropdown";

  /* #endregion */

  constructor() {
    this.instanceSuffix = MultiSelectDropdownComponent.instanceCounter
      .toString()
      .padStart(3, "0");
    MultiSelectDropdownComponent.instanceCounter += 1;
  }

  /* #region Public Methods */
  public ngOnInit(): void {
    this.displayItems = this.items;
    this.filteredOptions.set(this._filter(''));
    if (
      this.placeHolder === undefined ||
      this.placeHolder === null ||
      this.placeHolder.trim() === ""
    ) {
      this.placeHolder = "Common.Search.Placeholder";
    }
    this.searchTextboxControl.valueChanges.pipe(
      map((name) => this._filter(name)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(options => this.filteredOptions.set(options));
    if(BucketItemsComponent.BucketType!==""){
       this.disableSelection=true;
      this.disableClear=true;
    }
  }

  public ngOnChanges(): void {
    this.displayItems = this.items ?? [];
    this.searchTextboxControl.patchValue("", { emitEvent: false });
    this.filteredOptions.set(this._filter(''));
  }

  /*Method for multiselect option focus on click*/
  public ngAfterViewInit(): void {
    (<any>this.selector).baseonselect = (<any>this.selector)._onSelect;
    (<any>this.selector)._onSelect = (ev: any) => {
      (<any>this.selector).baseonselect(ev, false);
    };
  }
  public clearSearchWithData(){
    this.selectedItems = [];
    this.selectFormControl.patchValue(this.selectedItems);  
    this.clearOptions.emit(true);
  }

  public setSelectAll(checked: boolean) {
    this.selectAllChecked = checked;
    this.selectAllindeterminate = false;
    this.selectedItems.splice(0,this.selectedItems.length);
    if(checked){
      this.displayItems?.forEach((element: MultiSelect) => {
      this.selectedItems.push(element);
      });
    }
    this.handleSelectAll();
  }

  /** Method to handle Select All check-box */
  public handleSelectAll(): void {
    let count = 0;
    if (this.selectFormControl.value) {
      this.setSelectedValues();
      for (const displayItem of this.displayItemsForSearch) {
        let flag = false;
        this.selectFormControl.value.forEach((item: MultiSelect) => {
          if (item.id === displayItem.id) {
            count++;
            flag = true;
          }
        });
        if (!flag) {
          break;
        }
      }
      if (count === this.displayItemsForSearch.length) {
        this.IsChecked = true;
      } else {
        this.IsChecked = false;
      }
      this.selectFormControl.patchValue([]);
    this.selectFormControl.patchValue(this.selectedItems);
    this.setCheckBox();
    }
  }

  /** Method to handle de-select an option and to remove from selected list */
  public selectionChange(event: any): void {
    if (event.isUserInput && event.source.selected === false) {
      const index = this.selectedItems.findIndex(
        (selectedItem: any) => selectedItem.id === event.source.value.id
      );
      this.selectedItems.splice(index, 1);
    }
    this.setCheckBox();
  }

  private setCheckBox(){
    if(this.selectedItems.length===this.displayItems.length){
      this.selectAllChecked = true;
      this.selectAllindeterminate = false;
    }
    else if(this.selectedItems.length===0){
      this.selectAllChecked = false;
      this.selectAllindeterminate = false;
    }
    else{
      this.selectAllChecked = false;
      this.selectAllindeterminate = true;
    }
    this.firstOptionChecked = this.selectedItems.some(x=>x.id===1);
  }

  /** Method to support 'compareWith' when objects are used in mat-select */
  public equals(objOne: any, objTwo: any): boolean {
    return objOne && objTwo ? objOne.id === objTwo.id : objOne === objTwo;
  }

  /** Method to clear the search text */
  public clearSearch(): void {
    this.searchTextboxControl.patchValue("");
  }

  /** Method to handle close and emit the output */
  public close(opened: boolean): void {
    if (!opened.valueOf() && this.selectedItems !== undefined) {
      this.searchTextboxControl.patchValue("");
      this.setSelectedValues();
      this.selectedOptions.emit(this.selectFormControl.value);
    }
  }
  /* #endregion */

  /* #region Private Methods */
  /** Method to filter the list based on search text and to maintain existing selected values */
  private _filter(name: string): MultiSelect[] {
    const filterValue = name.toLowerCase();
    if (!this.selectedItems) {
      this.selectedItems = [];
    }
    // Set selected values to retain the selected checkbox state
    this.setSelectedValues();
    this.selectFormControl.patchValue(this.selectedItems);
    let itemsForSearch = this.items?.filter(x=>x.id!==-2);
    if(this.selectAllRequired && !itemsForSearch.some(x=>x.id==-1)){
      itemsForSearch?.push({id:-1,displayText:this.selectAllLabel,Code:this.selectAllLabel,Name:this.selectAllLabel});
    }
    this.displayItemsForSearch = itemsForSearch?.filter((option) =>
      new RegExp(filterValue, "gi").test(option.displayText)
    );
    this.handleSelectAll();
    if(this.displayItemsForSearch?.some(x=>x.id===-1)){
      this.showSelectAll = true;
      return this.displayItemsForSearch?.filter(x=>x.id!==-1);
    }
    else{
      this.showSelectAll = false;
      return this.displayItemsForSearch?.filter(x=>x.id!==-2);
    }
  }

  /** Method to store selected values as the mat-select re-constructs everytime whenever list option changes */
  private setSelectedValues(): void {
    if (
      this.selectFormControl.value &&
      this.selectFormControl.value.length > 0
    ) {
      this.updateSelectedItems(this.selectFormControl.value);
    }
  }

  private updateSelectedItems(list: MultiSelect[]): void {
    list.forEach((item: any) => {
      if (
        !this.selectedItems.find(
          (selectedItem: any) => selectedItem.id === item.id
        )
      ) {
        this.selectedItems.push(item);
      }
    });
  }
  /* #endregion */

  overlaydisplay(testsystem: MultiSelect[], e: any) {
    testsystem = testsystem.filter((x)=>x.id !== -1);
    e.stopPropagation();
    this.overlayPopupService.open(DescriptionOverlayComponent, {
      data: testsystem.map((x) => x.displayText),
      element: e,
      displayPointer: true,
    });
  }

  public emitSelectionChanged(): void {
    this.selectionChanged.emit(true);
  }

  public buildControlId(element: string, context?: string | number): string {
    const basePrefix = this.resolveBasePrefix();
    const tokens = [basePrefix, this.slugify(element)];
    if (context !== undefined && context !== null) {
      tokens.push(this.formatContext(context));
    }
    return tokens.join('-');
  }

  private resolveBasePrefix(): string {
    if (this.idPrefix && this.idPrefix.trim().length > 0) {
      return this.slugify(this.idPrefix);
    }
    return `${this.defaultFeatureToken}-${this.defaultSurfaceToken}-${this.instanceSuffix}`;
  }

  private formatContext(value: string | number): string {
    if (typeof value === 'number') {
      return value.toString().padStart(2, '0');
    }
    return this.slugify(value);
  }

  private slugify(value: string): string {
    return (value || 'na')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }
}
